/**
 * PortfoliosPage Component
 * Lists all portfolios with ability to create new ones
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTrackEvent, useTrackFeature, ANALYTICS_EVENTS } from '@resida/analytics';
import {
  listPortfolios,
  createPortfolio,
  type PortfolioWithStats,
} from '../lib/api';
import { Modal } from '../components/Modal';
import { PageHeader, EmptyState } from '../components/layout/index';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { formatDateTime } from '../utils/date';

export function PortfoliosPage() {
  const navigate = useNavigate();
  const track = useTrackEvent();
  useTrackFeature('portfolios', 'landlord');
  const [portfolios, setPortfolios] = useState<PortfolioWithStats[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [newPortfolioName, setNewPortfolioName] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const fetchPortfolios = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await listPortfolios();
      setPortfolios(response.portfolios);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load portfolios');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPortfolios();
  }, [fetchPortfolios]);

  const handleCreatePortfolio = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newPortfolioName.trim()) return;

    try {
      setIsCreating(true);
      setCreateError(null);
      await createPortfolio({ name: newPortfolioName.trim() });
      track(ANALYTICS_EVENTS.ONBOARDING_STEP_COMPLETED, { step: 'portfolio' });
      setNewPortfolioName('');
      setIsCreateModalOpen(false);
      await fetchPortfolios();
    } catch (err) {
      setCreateError(err instanceof Error ? err.message : 'Failed to create portfolio');
    } finally {
      setIsCreating(false);
    }
  };

  const handlePortfolioClick = (portfolioId: string) => {
    navigate(`/portfolios/${portfolioId}`);
  };

  if (isLoading) {
    return (
      <div className="max-w-4xl mx-auto pb-6">
        <div className="px-[var(--page-padding-x)]">
          <Skeleton className="h-10 w-48 mb-6" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <Skeleton className="h-36 rounded-lg" />
            <Skeleton className="h-36 rounded-lg" />
            <Skeleton className="h-36 rounded-lg" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto pb-6">
      <div className="px-[var(--page-padding-x)]">
        <PageHeader
          title="Portfolios"
          action={
            <Button size="sm" onClick={() => setIsCreateModalOpen(true)}>
              Create Portfolio
            </Button>
          }
        />

        {error && (
          <div className="flex justify-between items-center p-3 bg-destructive/5 border border-destructive/20 rounded-lg mb-6">
            <p className="text-sm text-destructive m-0">{error}</p>
            <Button
              variant="outline"
              className="text-destructive"
              onClick={fetchPortfolios}
            >
              Retry
            </Button>
          </div>
        )}

        {portfolios.length === 0 ? (
          <EmptyState
            title="No Portfolios Yet"
            description="Create your first portfolio to start organizing your properties."
            action={
              <Button onClick={() => setIsCreateModalOpen(true)}>
                Create Your First Portfolio
              </Button>
            }
          />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {portfolios.map((portfolio) => (
              <div
                key={portfolio.id}
                className="bg-card rounded-lg p-5 border border-border cursor-pointer hover:bg-accent transition-colors"
                onClick={() => handlePortfolioClick(portfolio.id)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    handlePortfolioClick(portfolio.id);
                  }
                }}
              >
                <h3 className="text-base font-semibold text-foreground mb-4">{portfolio.name}</h3>
                <div className="flex gap-6 mb-4">
                  <div className="flex flex-col">
                    <span className="text-2xl font-bold text-primary">{portfolio.propertyCount ?? 0}</span>
                    <span className="text-sm text-muted-foreground">Properties</span>
                  </div>
                  <div className="flex flex-col">
                    <span className="text-2xl font-bold text-primary">{portfolio.unitCount ?? 0}</span>
                    <span className="text-sm text-muted-foreground">Units</span>
                  </div>
                </div>
                <p className="text-sm text-muted-foreground m-0">
                  Created {formatDateTime(portfolio.createdAt, { year: 'numeric', month: 'short', day: 'numeric' })}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>

      <Modal
        isOpen={isCreateModalOpen}
        onClose={() => {
          setIsCreateModalOpen(false);
          setNewPortfolioName('');
          setCreateError(null);
        }}
        title="Create New Portfolio"
      >
        <form onSubmit={handleCreatePortfolio}>
          {createError && (
            <div className="p-3 mb-4 bg-destructive/5 border border-destructive/20 rounded-md text-destructive text-sm">
              {createError}
            </div>
          )}
          <div className="mb-5">
            <label
              htmlFor="portfolioName"
              className="block mb-2 text-sm font-medium text-foreground"
            >
              Portfolio Name
            </label>
            <input
              id="portfolioName"
              type="text"
              value={newPortfolioName}
              onChange={(e) => setNewPortfolioName(e.target.value)}
              placeholder="e.g., Downtown Properties"
              className="w-full px-3 py-2 text-sm border border-input rounded-md focus:outline-none focus:ring-2 focus:ring-ring bg-background text-foreground"
              autoFocus
              required
            />
          </div>
          <div className="flex justify-end gap-3">
            <Button
              type="button"
              variant="ghost"
              onClick={() => {
                setIsCreateModalOpen(false);
                setNewPortfolioName('');
                setCreateError(null);
              }}
              disabled={isCreating}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isCreating || !newPortfolioName.trim()}
            >
              {isCreating ? 'Creating...' : 'Create Portfolio'}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}

export default PortfoliosPage;
